import React, { useState, useEffect } from 'react';
import { 
  Bell, 
  Save, 
  Eye, 
  EyeOff, 
  Type, 
  Link as LinkIcon, 
  Clock, 
  Phone, 
  MapPin,
  ArrowRight,
  ArrowLeft,
  Sparkles,
  Info
} from 'lucide-react';
import axios from 'axios';

const NotificationBar = () => {
  const [settings, setSettings] = useState({
    enabled: false,
    mode: 'business_info', // 'custom_message' or 'business_info'
    // Custom message settings
    message_text: '',
    animation_type: 'none', // 'none', 'scroll_left', 'scroll_right', 'blink', 'fade'
    show_link_button: false,
    link_button_text: 'Learn More',
    link_button_url: '',
    background_color: '#0891b2',
    text_color: '#ffffff',
    // Business info settings (these come from header config but we can override visibility here)
    show_phone: true,
    show_address: true,
    show_hours_status: true
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [previewVisible, setPreviewVisible] = useState(false);
  
  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/notification-bar-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setSettings(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      console.error('Error fetching notification bar settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API_BASE_URL}/api/notification-bar-settings`,
        settings,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessage({ type: 'success', text: 'Notification bar settings saved successfully!' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      console.error('Error saving settings:', error);
      setMessage({ type: 'error', text: 'Failed to save settings. Please try again.' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } finally {
      setSaving(false);
    }
  };

  const getAnimationStyle = () => {
    switch (settings.animation_type) {
      case 'scroll_left':
        return 'animate-marquee-left';
      case 'scroll_right':
        return 'animate-marquee-right';
      case 'blink':
        return 'animate-blink';
      case 'fade':
        return 'animate-fade-pulse';
      default:
        return '';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <style>{`
        @keyframes marquee-left {
          0% { transform: translateX(100%); }
          100% { transform: translateX(-100%); }
        }
        @keyframes marquee-right {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        @keyframes blink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0; }
        }
        @keyframes fade-pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }
        .animate-marquee-left {
          animation: marquee-left 15s linear infinite;
        }
        .animate-marquee-right {
          animation: marquee-right 15s linear infinite;
        }
        .animate-blink {
          animation: blink 1.5s ease-in-out infinite;
        }
        .animate-fade-pulse {
          animation: fade-pulse 2s ease-in-out infinite;
        }
      `}</style>
      
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          {/* Header - Compact Design like Configure Hours */}
          <div className="bg-white border-b border-gray-200">
            <div className="p-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="min-w-0">
                  <h2 className="text-lg font-semibold text-gray-900">
                    Notification Bar
                  </h2>
                </div>
                
                {/* Enable Toggle and Save Button */}
                <div className="flex flex-wrap items-center gap-4">
                  {/* Enable Toggle */}
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-700">Enable</span>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.enabled}
                        onChange={() => setSettings(prev => ({ ...prev, enabled: !prev.enabled }))}
                        className="sr-only peer"
                      />
                      <div 
                        className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all"
                        style={settings.enabled ? { 
                          backgroundColor: 'var(--theme-primary)',
                          boxShadow: '0 0 0 4px rgba(41, 173, 211, 0.2)' 
                        } : {}}
                      ></div>
                    </label>
                  </div>

                  {/* Save Button */}
                  <button
                    onClick={saveSettings}
                    disabled={saving}
                    className="inline-flex items-center px-4 py-2 text-white rounded-lg text-sm font-medium transition-all duration-200 shadow-sm hover:shadow-md disabled:opacity-50"
                    style={{ backgroundColor: 'var(--theme-primary)' }}
                  >
                    <Save className="h-4 w-4 mr-2" />
                    {saving ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Content Area */}
          <div className="p-6">
            {/* Message */}
            {message.text && (
              <div className={`mb-6 rounded-md p-4 ${
                message.type === 'success' 
                  ? 'bg-green-50 border border-green-200' 
                  : 'bg-red-50 border border-red-200'
              }`}>
                <p className={`text-sm font-medium ${
                  message.type === 'success' ? 'text-green-800' : 'text-red-800'
                }`}>
                  {message.text}
                </p>
              </div>
            )}

            {settings.enabled && (
              <>
                {/* Mode Selection */}
                <div className="mb-6">
                  <h3 className="text-sm font-medium text-gray-900 mb-3">Display Mode</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Custom Message Mode */}
                    <div
                      onClick={() => setSettings(prev => ({ ...prev, mode: 'custom_message' }))}
                      className={`cursor-pointer border-2 rounded-lg p-4 transition-all ${
                        settings.mode === 'custom_message'
                          ? 'border-gray-300 bg-gray-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      style={settings.mode === 'custom_message' ? { 
                        borderColor: 'var(--theme-primary)',
                        backgroundColor: 'rgba(41, 173, 211, 0.05)'
                      } : {}}
                    >
                      <div className="flex items-center gap-3 mb-2">
                        <div 
                          className="p-2 rounded-lg"
                          style={settings.mode === 'custom_message' ? { 
                            backgroundColor: 'var(--theme-primary)',
                            color: 'white'
                          } : { backgroundColor: '#f3f4f6', color: '#4b5563' }}
                        >
                          <Type className="h-4 w-4" />
                        </div>
                        <h4 className="text-sm font-medium text-gray-900">Custom Message</h4>
                      </div>
                      <p className="text-xs text-gray-500">
                        Display a custom promotional message with optional animations
                      </p>
                    </div>

                    {/* Business Info Mode */}
                    <div
                      onClick={() => setSettings(prev => ({ ...prev, mode: 'business_info' }))}
                      className={`cursor-pointer border-2 rounded-lg p-4 transition-all ${
                        settings.mode === 'business_info'
                          ? 'border-gray-300 bg-gray-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      style={settings.mode === 'business_info' ? { 
                        borderColor: 'var(--theme-primary)',
                        backgroundColor: 'rgba(41, 173, 211, 0.05)'
                      } : {}}
                    >
                      <div className="flex items-center gap-3 mb-2">
                        <div 
                          className="p-2 rounded-lg"
                          style={settings.mode === 'business_info' ? { 
                            backgroundColor: 'var(--theme-primary)',
                            color: 'white'
                          } : { backgroundColor: '#f3f4f6', color: '#4b5563' }}
                        >
                          <Info className="h-4 w-4" />
                        </div>
                        <h4 className="text-sm font-medium text-gray-900">Business Information</h4>
                      </div>
                      <p className="text-xs text-gray-500">
                        Show phone number, address, and hours status
                      </p>
                    </div>
                  </div>
                </div>

                {/* Custom Message Settings */}
                {settings.mode === 'custom_message' && (
                  <div className="border-t border-gray-200 pt-6 mb-6">
                    <h3 className="text-sm font-medium text-gray-900 mb-4">Custom Message Settings</h3>
                    
                    {/* Message Text */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Notification Message *
                      </label>
                      <textarea
                        value={settings.message_text}
                        onChange={(e) => setSettings(prev => ({ ...prev, message_text: e.target.value }))}
                        placeholder="e.g., 🎉 Store Wide 50% OFF - Sale Ends Soon!"
                        rows={2}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>

                    {/* Animation Type */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Animation Effect
                      </label>
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                        {[
                          { value: 'none', label: 'None', icon: null },
                          { value: 'scroll_left', label: 'Scroll Left', icon: ArrowLeft },
                          { value: 'scroll_right', label: 'Scroll Right', icon: ArrowRight },
                          { value: 'blink', label: 'Blink', icon: Sparkles },
                          { value: 'fade', label: 'Fade Pulse', icon: Sparkles }
                        ].map((option) => (
                          <button
                            key={option.value}
                            type="button"
                            onClick={() => setSettings(prev => ({ ...prev, animation_type: option.value }))}
                            className={`p-2 border rounded-lg text-center transition-all ${
                              settings.animation_type === option.value
                                ? 'border-gray-300 bg-gray-50'
                                : 'border-gray-200 hover:border-gray-300 text-gray-600'
                            }`}
                            style={settings.animation_type === option.value ? { 
                              borderColor: 'var(--theme-primary)',
                              backgroundColor: 'rgba(41, 173, 211, 0.05)'
                            } : {}}
                          >
                            {option.icon && <option.icon className="h-3.5 w-3.5 mx-auto mb-1" />}
                            <span className="text-xs font-medium">{option.label}</span>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Colors */}
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Background Color
                        </label>
                        <div className="flex gap-2">
                          <input
                            type="color"
                            value={settings.background_color}
                            onChange={(e) => setSettings(prev => ({ ...prev, background_color: e.target.value }))}
                            className="w-10 h-9 rounded cursor-pointer border border-gray-300"
                          />
                          <input
                            type="text"
                            value={settings.background_color}
                            onChange={(e) => setSettings(prev => ({ ...prev, background_color: e.target.value }))}
                            className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Text Color
                        </label>
                        <div className="flex gap-2">
                          <input
                            type="color"
                            value={settings.text_color}
                            onChange={(e) => setSettings(prev => ({ ...prev, text_color: e.target.value }))}
                            className="w-10 h-9 rounded cursor-pointer border border-gray-300"
                          />
                          <input
                            type="text"
                            value={settings.text_color}
                            onChange={(e) => setSettings(prev => ({ ...prev, text_color: e.target.value }))}
                            className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Link Button */}
                    <div className="pt-4 border-t border-gray-100">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <span className="text-sm font-medium text-gray-700">Add Link Button</span>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={settings.show_link_button}
                            onChange={() => setSettings(prev => ({ ...prev, show_link_button: !prev.show_link_button }))}
                            className="sr-only peer"
                          />
                          <div 
                            className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all"
                            style={settings.show_link_button ? { 
                              backgroundColor: 'var(--theme-primary)',
                              boxShadow: '0 0 0 4px rgba(41, 173, 211, 0.2)' 
                            } : {}}
                          ></div>
                        </label>
                      </div>

                      {settings.show_link_button && (
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Button Text
                            </label>
                            <input
                              type="text"
                              value={settings.link_button_text}
                              onChange={(e) => setSettings(prev => ({ ...prev, link_button_text: e.target.value }))}
                              placeholder="Learn More"
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Button URL
                            </label>
                            <input
                              type="text"
                              value={settings.link_button_url}
                              onChange={(e) => setSettings(prev => ({ ...prev, link_button_url: e.target.value }))}
                              placeholder="/sale or https://..."
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Business Info Settings */}
                {settings.mode === 'business_info' && (
                  <div className="border-t border-gray-200 pt-6 mb-6">
                    <h3 className="text-sm font-medium text-gray-900 mb-3">Business Information Settings</h3>
                    
                    <div className="space-y-3">
                      <label className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer">
                        <div className="flex items-center gap-3">
                          <Phone className="h-4 w-4 text-gray-500" />
                          <span className="text-sm text-gray-700">Show Phone Number</span>
                        </div>
                        <input
                          type="checkbox"
                          checked={settings.show_phone}
                          onChange={(e) => setSettings(prev => ({ ...prev, show_phone: e.target.checked }))}
                          className="h-4 w-4 rounded focus:ring-blue-500"
                          style={{ accentColor: 'var(--theme-primary)' }}
                        />
                      </label>

                      <label className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer">
                        <div className="flex items-center gap-3">
                          <MapPin className="h-4 w-4 text-gray-500" />
                          <span className="text-sm text-gray-700">Show Address</span>
                        </div>
                        <input
                          type="checkbox"
                          checked={settings.show_address}
                          onChange={(e) => setSettings(prev => ({ ...prev, show_address: e.target.checked }))}
                          className="h-4 w-4 rounded focus:ring-blue-500"
                          style={{ accentColor: 'var(--theme-primary)' }}
                        />
                      </label>

                      <label className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer">
                        <div className="flex items-center gap-3">
                          <Clock className="h-4 w-4 text-gray-500" />
                          <span className="text-sm text-gray-700">Show Hours Status</span>
                        </div>
                        <input
                          type="checkbox"
                          checked={settings.show_hours_status}
                          onChange={(e) => setSettings(prev => ({ ...prev, show_hours_status: e.target.checked }))}
                          className="h-4 w-4 rounded focus:ring-blue-500"
                          style={{ accentColor: 'var(--theme-primary)' }}
                        />
                      </label>
                    </div>

                    {/* Background Color for Business Info */}
                    <div className="mt-4 pt-4 border-t border-gray-100">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Background Color
                      </label>
                      <div className="flex gap-2 max-w-xs">
                        <input
                          type="color"
                          value={settings.background_color}
                          onChange={(e) => setSettings(prev => ({ ...prev, background_color: e.target.value }))}
                          className="w-10 h-9 rounded cursor-pointer border border-gray-300"
                        />
                        <input
                          type="text"
                          value={settings.background_color}
                          onChange={(e) => setSettings(prev => ({ ...prev, background_color: e.target.value }))}
                          className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Preview */}
                <div className="border-t border-gray-200 pt-6">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-medium text-gray-900">Preview</h3>
                    <button
                      onClick={() => setPreviewVisible(!previewVisible)}
                      className="flex items-center gap-2 text-xs text-gray-600 hover:text-gray-900"
                    >
                      {previewVisible ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                      {previewVisible ? 'Hide' : 'Show'}
                    </button>
                  </div>

                  {previewVisible && (
                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                      {settings.mode === 'custom_message' ? (
                        <div
                          className="py-2 px-4 text-center overflow-hidden"
                          style={{ backgroundColor: settings.background_color, color: settings.text_color }}
                        >
                          <div className="flex items-center justify-center gap-4">
                            <span className={`whitespace-nowrap text-sm ${getAnimationStyle()}`}>
                              {settings.message_text || 'Your notification message will appear here'}
                            </span>
                            {settings.show_link_button && settings.link_button_text && (
                              <button className="px-3 py-1 bg-white/20 hover:bg-white/30 rounded text-xs font-medium whitespace-nowrap">
                                {settings.link_button_text}
                              </button>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div
                          className="py-2 px-4 text-white text-xs"
                          style={{ backgroundColor: settings.background_color }}
                        >
                          <div className="flex justify-between items-center">
                            <div className="flex items-center gap-4">
                              {settings.show_phone && (
                                <div className="flex items-center gap-1">
                                  <Phone className="h-3.5 w-3.5" />
                                  <span>(555) 123-4567</span>
                                </div>
                              )}
                              {settings.show_address && (
                                <div className="flex items-center gap-1">
                                  <MapPin className="h-3.5 w-3.5" />
                                  <span>123 Main St, City</span>
                                </div>
                              )}
                            </div>
                            {settings.show_hours_status && (
                              <div className="flex items-center gap-1">
                                <Clock className="h-3.5 w-3.5" />
                                <span>Open Now</span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationBar;
